import { useMemo } from "react";

import { Icon } from "@web-app/components";
import { State } from "@web-app/types";

export default function SelectedMarketPills({
  includeNullState = false,
  onRemoveState,
  stateIds,
  statesMap,
  variant = "gray",
}: {
  includeNullState?: boolean;
  onRemoveState: (stateId: string) => void;
  stateIds: string[];
  statesMap?: Map<string, State>;
  variant?: "blue" | "gray";
}) {
  const colorClasses = {
    blue: {
      hover: "hover:text-blue-900",
      pill: "bg-blue-100 text-blue-700",
    },
    gray: {
      hover: "hover:text-gray-900",
      pill: "bg-gray-100 text-gray-700",
    },
  };

  const selectedStates = useMemo(() => {
    if (!stateIds.length) return [];
    const states = stateIds
      .map((id) => {
        if (includeNullState && id === "null") {
          return { id: "null", name: "No Markets" };
        }
        return statesMap?.get(id);
      })
      .filter((state): state is State | { id: string; name: string } => !!state)
      .sort((a, b) => a.name.localeCompare(b.name));
    return states;
  }, [stateIds, statesMap, includeNullState]);

  const maxVisiblePills = 3;
  const visibleStates = selectedStates.slice(0, maxVisiblePills);
  const remainingCount = selectedStates.length - maxVisiblePills;

  const colors = colorClasses[variant];

  return (
    <div className="flex flex-wrap gap-2">
      {visibleStates.map((state) => (
        <div
          key={state.id}
          className={`inline-flex items-center gap-1 ${colors.pill} text-[10px] lg:text-xs px-2 py-1 rounded-md max-w-[140px]`}
        >
          <span className="truncate">{state.name}</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveState(state.id);
            }}
            className={`ml-1 ${colors.hover} focus:outline-none flex self-center flex-shrink-0`}
          >
            <Icon name="xMark" className="h-3 w-3" />
          </button>
        </div>
      ))}
      {remainingCount > 0 && (
        <div
          className={`inline-flex items-center ${colors.pill} text-[10px] lg:text-xs px-2 py-1 rounded-md`}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  );
}
